import React from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { inr } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetClose } from "@/components/ui/sheet";
import { X, Plus, Minus, Trash2, ShoppingBag, ArrowRight, Truck } from "lucide-react";

/**
 * CartDrawer — Swiggy-style slide-over cart popup.
 * Opened from the floating StickyCartBar "View Cart" button so shoppers can
 * review/edit items and close to continue browsing without leaving the page.
 */
export function CartDrawer({ open, onOpenChange }) {
  const { items, totals, setQty, removeItem } = useCart();
  const navigate = useNavigate();

  const go = (path) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="cart-drawer"
        className="w-full sm:max-w-md bg-hyd-bg border-l border-hyd-border p-0 flex flex-col [&>button]:hidden"
      >
        <SheetHeader className="px-4 py-3 border-b border-hyd-border flex flex-row items-center justify-between space-y-0">
          <SheetTitle className="font-display text-xl font-bold text-hyd-text flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-hyd-gold" />
            Your cart
            <span className="text-xs font-sans font-bold bg-hyd-gold/15 text-hyd-gold px-2 py-0.5 rounded-full">
              {totals.count} item{totals.count === 1 ? "" : "s"}
            </span>
          </SheetTitle>
          <SheetClose asChild>
            <button
              data-testid="cart-drawer-close"
              aria-label="Close cart"
              className="w-9 h-9 grid place-items-center rounded-full border border-hyd-border text-hyd-text2 hover:text-hyd-red hover:border-hyd-red/50 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </SheetClose>
        </SheetHeader>

        {/* Minimum-order and delivery hints */}
        {!totals.minimumOrderMet ? (
          <div className="px-4 py-2 bg-hyd-red/10 border-b border-hyd-border text-[11px] text-hyd-red flex items-center gap-1.5">
            <Truck className="w-3.5 h-3.5" />
            Minimum order value {inr(totals.minimumOrderValue)}. Add {inr(totals.minimumOrderRemaining)} to place your order.
          </div>
        ) : (
          <div className="px-4 py-2 bg-emerald-500/10 border-b border-hyd-border text-[11px] text-emerald-500 flex items-center gap-1.5">
            <Truck className="w-3.5 h-3.5" />
            You unlocked FREE delivery
          </div>
        )}

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {items.map((it) => (
            <div key={it.key} data-testid={`cart-drawer-item-${it.key}`} className="flex gap-3 bg-hyd-panel border border-hyd-border rounded-2xl p-3">
              <div className="w-14 h-14 rounded-xl bg-hyd-panel2 overflow-hidden shrink-0">
                {it.image_url && <img src={it.image_url} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-hyd-text line-clamp-2">{it.name}</div>
                <div className="text-hyd-gold font-bold text-sm mt-0.5">{inr(it.unit_price)}</div>
              </div>
              <div className="flex flex-col items-end justify-between shrink-0">
                <button
                  onClick={() => removeItem(it.kind, it.ref_id)}
                  aria-label="Remove item"
                  className="text-hyd-text3 hover:text-hyd-red transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                <div className="flex items-center gap-1.5 bg-hyd-bg border border-hyd-border rounded-full px-1.5 py-0.5">
                  <button onClick={() => setQty(it.kind, it.ref_id, it.qty - 1)} aria-label="Decrease" className="w-6 h-6 grid place-items-center text-hyd-red rounded-full hover:bg-hyd-panel">
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="min-w-[18px] text-center text-sm font-bold text-hyd-text">{it.qty}</span>
                  <button onClick={() => setQty(it.kind, it.ref_id, it.qty + 1)} aria-label="Increase" disabled={it.stock != null && it.qty >= Number(it.stock)} className="w-6 h-6 grid place-items-center text-hyd-gold rounded-full hover:bg-hyd-panel disabled:opacity-40 disabled:cursor-not-allowed">
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div className="py-12 text-center text-hyd-text2 text-sm">Your cart is empty</div>
          )}
        </div>

        {/* Totals + actions */}
        {items.length > 0 && (
          <div className="border-t border-hyd-border px-4 py-3 space-y-3 bg-hyd-panel/60">
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-hyd-text2">
                <span>Subtotal</span><span className="text-hyd-text">{inr(totals.subtotal)}</span>
              </div>
              {totals.savings > 0 && (
                <div className="flex justify-between text-emerald-500 text-xs">
                  <span>You save</span><span>- {inr(totals.savings)}</span>
                </div>
              )}
              <div className="flex justify-between text-hyd-text2">
                <span>Delivery</span>
                <span className={totals.deliveryFee === 0 ? "text-emerald-500 font-bold" : "text-hyd-text"}>
                  {totals.deliveryFee === 0 ? "FREE" : inr(totals.deliveryFee)}
                </span>
              </div>
              <div className="flex justify-between font-bold text-hyd-text pt-1 border-t border-hyd-border/60">
                <span>To pay</span><span>{inr(totals.total)}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="cart-drawer-view-cart"
                onClick={() => go("/cart")}
                className="flex-1 h-11 rounded-full border border-hyd-gold/60 text-hyd-gold font-bold text-xs uppercase tracking-widest hover:bg-hyd-gold/10 transition-colors"
              >
                View cart page
              </button>
              <button
                data-testid="cart-drawer-checkout"
                onClick={() => go("/checkout")}
                disabled={!totals.minimumOrderMet}
                className="flex-[1.4] h-11 rounded-full bg-hyd-red hover:bg-hyd-redDim text-white font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-hyd-red"
              >
                Checkout <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
